import React, { useState, useEffect } from 'react';
import { fetchJson } from '../../utils/fetchJson';
import { Panel, KpiGrid, KpiCard, Badge, Button } from '../../components/design';

const SEVERITY_COLORS = {
  critical: 'red',
  high: 'amber',
  medium: 'amber',
  low: 'gray',
};

export default function ServerVulnerabilitiesTab({ serverId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetchJson(`/api/vulnerabilities/registry?server_id=${serverId}&limit=200`);
        if (!cancelled) setData(res);
      } catch (e) {
        console.error('Failed to load vulns:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [serverId]);

  if (loading) {
    return (
      <div className="muted" style={{ textAlign: 'center', padding: '48px 0' }}>Loading...</div>
    );
  }

  const allItems = data?.items || [];
  const packages = {
    total: allItems.filter(i => i.source === 'package').length,
    items: allItems.filter(i => i.source === 'package'),
  };
  const cves = {
    total: allItems.length - packages.total,
    items: allItems.filter(i => i.source !== 'package'),
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <KpiGrid>
        <KpiCard label="Total" value={data?.total || 0} countUp icon="shield" iconBg="rgba(99,102,241,0.10)" iconColor="#6366f1" />
        <KpiCard label="High" value={data?.high || 0} countUp icon="alert-triangle" iconBg="rgba(248,113,113,0.10)" iconColor="#f87171" />
        <KpiCard label="Medium" value={data?.medium || 0} countUp icon="shield-alert" iconBg="rgba(251,191,36,0.10)" iconColor="#fbbf24" />
      </KpiGrid>

      <Section title="Package Updates" icon="📦" data={packages} serverId={serverId}
        columns={['Package', 'Advisory']} showUpgrade={true}
        renderRow={item => ({
          cells: [item.package_name || '—', item.title || item.cve_id || '—'],
          upgradePkg: item.package_name,
        })} />

      <Section title="CVE Vulnerabilities" icon="🛡️" data={cves} serverId={serverId}
        columns={['CVE', 'CVSS']}
        renderRow={item => ({
          cells: [item.cve_id || '—', (item.cvss_score || 0).toFixed(1)],
        })} />
    </div>
  );
}

function Section({ title, icon, data, columns, renderRow, showUpgrade, serverId }) {
  const [upgrading, setUpgrading] = useState({});
  const [confirmPkg, setConfirmPkg] = useState(null);

  const handleUpgrade = async (pkg) => {
    if (!confirmPkg) {
      setConfirmPkg(pkg);
      return;
    }
    setConfirmPkg(null);
    setUpgrading(prev => ({ ...prev, [pkg]: 'upgrading' }));
    try {
      const resp = await fetch(`/api/servers/${serverId}/upgrade`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ package: pkg }),
      });
      const result = await resp.json();
      setUpgrading(prev => ({ ...prev, [pkg]: result.task_id ? 'queued' : false }));
    } catch (e) {
      setUpgrading(prev => ({ ...prev, [pkg]: false }));
    }
  };
  if (!data || data.total === 0) {
    return (
      <Panel title={`${icon} ${title}`}>
        <div className="muted" style={{ textAlign: 'center', padding: '20px 0', fontSize: 12 }}>
          No {title.toLowerCase()} detected
        </div>
      </Panel>
    );
  }

  return (
    <Panel title={`${icon} ${title}`} sub={`${data.total} open`} noPad>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              {columns.map(c => (
                <th key={c}>{c}</th>
              ))}
              <th style={{ width: 64 }}>Sev</th>
              {showUpgrade && <th style={{ width: 80 }}>Action</th>}
            </tr>
          </thead>
          <tbody>
            {(data?.items || []).slice(0, 20).map(item => {
              const row = renderRow(item) || {};
              const pkg = row?.upgradePkg || '';
              const cells = row?.cells || [];
              return (
                <tr key={item.id}>
                  {cells.map((cell, i) => (
                    <td key={i}>{cell}</td>
                  ))}
                  <td>
                    <Badge tone={SEVERITY_COLORS[item.severity] || 'gray'}>{item.severity?.charAt(0).toUpperCase()}</Badge>
                  </td>
                  {showUpgrade && (
                    <td>
                        {pkg ? (
                        confirmPkg === pkg ? (
                          <div className="flex items-center gap-1">
                            <Button variant="danger" size="sm" onClick={() => handleUpgrade(pkg)}>Confirm</Button>
                            <button onClick={() => setConfirmPkg(null)}
                              style={{ cursor: 'pointer', background: 'none', border: 'none', color: 'var(--text-3)' }}>
                              ✕
                            </button>
                          </div>
                        ) : upgrading?.[pkg] === 'upgrading' ? (
                          <Badge tone="blue">Upgrading</Badge>
                        ) : upgrading?.[pkg] === 'queued' ? (
                          <Badge tone="green">✓ Queued</Badge>
                        ) : (
                          <Button variant="primary" size="sm" onClick={() => handleUpgrade(pkg)}
                            disabled={!!(upgrading && pkg && upgrading[pkg])}>
                            Upgrade
                          </Button>
                        )
                      ) : null}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {data.items.length > 20 && (
        <div className="muted" style={{ padding: '6px 12px', fontSize: 10, borderTop: '1px solid var(--glass-border)' }}>
          +{data.items.length - 20} more — see full list on <a href="#vulnerabilities" style={{ color: 'var(--accent-1)' }}>Vulnerabilities page</a>
        </div>
      )}
    </Panel>
  );
}
